/**
 * PL3 — merge document-extracted claims into a SiteContext.
 *
 * The legacy site-context flow runs `extractClaims` ONLY when the user
 * saves prose in `SiteContextInput`. Document-extracted claims (PL3)
 * arrive on a different code path — the doc-upload pipeline emits
 * claims after Gemini parses `textContent` — so we need a small merger
 * that:
 *
 *   1. Stamps `source: 'document-extracted'` + `sourceDocId` on every
 *      incoming claim so the UI can render a "from {fileName}" chip.
 *   2. De-duplicates against the existing claim set by `(category, claim)`
 *      so re-uploading the same doc (or admin re-extracting) doesn't
 *      double-stamp the panel. When a fresh claim matches an existing
 *      one and the existing one is `user-typed`, the merger preserves
 *      the user-typed claim (their authoring wins) but appends the
 *      `sourceDocId` to a `mergedFromDocIds` audit field. When both
 *      sides are `document-extracted`, the merger keeps the higher
 *      `extractorConfidence` and tags the result `'merged'`.
 *
 * UAE-3 extension: when the incoming claim carries `requirementId` (a
 * user-authored Coverage Matrix claim), the merger does NOT collapse it
 * into any same-text claim with a different provenance. Two claims are
 * "the same" only when they share `(category, claim, requirementId)`
 * together — so an authored zoning claim and a doc-extracted zoning
 * claim stay as separate entries in `claims[]` and the conflict surface
 * can flag the disagreement.
 *
 * Pure / sync — the caller (store action) handles the Firestore write
 * + audit emission.
 */
import type { ExtractedClaim, SiteContext } from '../../types/siteContext';

export interface MergeClaimsOptions {
  /** PL3 — document id every incoming claim was extracted from. Omit
   *  for UAE-3 user-authored writes; the writer stamps the claim
   *  directly. */
  sourceDocId?: string;
  /** Provenance label written onto every incoming claim. */
  provenance: 'document-extracted' | 'user-typed';
  /**
   * UAE-3 — when true, an incoming claim whose `(category, requirementId)`
   * tuple already exists in `current.claims[]` REPLACES the existing
   * claim and pushes the old version onto `priorVersions` (capped at
   * 10). Used by `writeAuthoredClaim` for the "user edited their
   * authored evidence" path. Default false preserves PL3 dedupe
   * semantics.
   */
  replaceByRequirementId?: boolean;
}

export interface MergeClaimsResult {
  /** Updated claim array — original order preserved; new claims appended. */
  claims: ExtractedClaim[];
  /** How many claims were appended (not de-duplicated against existing). */
  added: number;
  /** How many incoming claims merged into an existing matching claim. */
  merged: number;
}

/**
 * Stable identity key for de-dupe. Two claims are "the same" when they
 * share category, a normalised claim string, AND the same
 * `requirementId` slot (both unset, or both pointing to the same
 * Coverage Matrix row). Avoids treating "Permitted for 5 MGD" and
 * "Permitted for 5 mgd" as distinct AND prevents an authored zoning
 * claim from colliding with a doc-extracted zoning claim on the same
 * requirement — UAE-3 keeps both so the conflict surface can flag the
 * disagreement.
 */
function identityKey(
  c: Pick<ExtractedClaim, 'category' | 'claim' | 'requirementId'>,
): string {
  return `${c.category}::${c.requirementId ?? ''}::${(c.claim ?? '').toLowerCase().trim()}`;
}

/**
 * UAE-3 — key for the "replace by requirement" path used by
 * `writeAuthoredClaim`. Two authored claims target the same Coverage
 * Matrix slot when they share `(category, requirementId)` even if the
 * user re-phrased the claim text. The latter write wins; the prior
 * version is archived on `priorVersions`.
 */
function requirementKey(c: Pick<ExtractedClaim, 'category' | 'requirementId'>): string | null {
  if (!c.requirementId) return null;
  return `${c.category}::req::${c.requirementId}`;
}

/** UAE-3 — cap for archived prior versions of an authored claim. */
const MAX_PRIOR_VERSIONS = 10;

export function mergeClaimsIntoSiteContext(
  current: SiteContext,
  incoming: ExtractedClaim[],
  options: MergeClaimsOptions,
): MergeClaimsResult {
  if (incoming.length === 0) {
    return { claims: current.claims, added: 0, merged: 0 };
  }
  const existingByKey = new Map<string, number>();
  // UAE-3 — track existing claims by (category, requirementId) so a
  // re-author of the same Coverage Matrix slot can replace-in-place
  // even when the claim text changed.
  const existingByRequirement = new Map<string, number>();
  current.claims.forEach((c, i) => {
    existingByKey.set(identityKey(c), i);
    const rk = requirementKey(c);
    if (rk) existingByRequirement.set(rk, i);
  });

  const stamped: ExtractedClaim[] = incoming.map(c => {
    const next: ExtractedClaim = {
      ...c,
      source: options.provenance,
    };
    // PL3 — only stamp sourceDocId for doc-extracted writes. The
    // user-authored (UAE-3) path leaves it undefined; the writer sets
    // any provenance fields it cares about.
    if (options.provenance === 'document-extracted' && options.sourceDocId !== undefined) {
      next.sourceDocId = options.sourceDocId;
    }
    return next;
  });

  const out = current.claims.slice();
  let added = 0;
  let merged = 0;

  for (const fresh of stamped) {
    // UAE-3 — replace-by-requirement path for authored edits. Fires
    // BEFORE the identityKey lookup so the user can rewrite the claim
    // text and still replace the prior version on the same slot.
    if (options.replaceByRequirementId) {
      const rk = requirementKey(fresh);
      if (rk) {
        const reqIdx = existingByRequirement.get(rk);
        if (reqIdx !== undefined) {
          const prior = out[reqIdx];
          // Archive the prior version onto priorVersions, capped at
          // MAX_PRIOR_VERSIONS, oldest dropped first.
          const archive = [...(prior.priorVersions ?? []), { ...prior, priorVersions: undefined }]
            .slice(-MAX_PRIOR_VERSIONS);
          out[reqIdx] = { ...fresh, priorVersions: archive };
          // Refresh the identityKey index so subsequent dedupe in this
          // same incoming batch sees the new claim text.
          existingByKey.set(identityKey(fresh), reqIdx);
          merged += 1;
          continue;
        }
      }
    }

    const key = identityKey(fresh);
    const existingIdx = existingByKey.get(key);
    if (existingIdx === undefined) {
      out.push(fresh);
      existingByKey.set(key, out.length - 1);
      const rk = requirementKey(fresh);
      if (rk) existingByRequirement.set(rk, out.length - 1);
      added += 1;
      continue;
    }
    const existing = out[existingIdx];
    // PL3 — user-typed claims always win the value; we only annotate
    // the doc reference so the UI can show "also extracted from {doc}".
    if (existing.source === 'user-typed' || existing.source === undefined) {
      // Don't overwrite user-authored content. Skip — but count as merged
      // so caller telemetry reflects the de-dupe.
      merged += 1;
      continue;
    }
    // Both sides doc-extracted (or merged) — keep the higher-confidence
    // text and tag the survivor as 'merged' so the UI shows a
    // "multiple sources" badge.
    const winner = (fresh.extractorConfidence ?? 0) > (existing.extractorConfidence ?? 0)
      ? fresh
      : existing;
    out[existingIdx] = {
      ...winner,
      source: 'merged',
    };
    merged += 1;
  }

  return { claims: out, added, merged };
}
